import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, getDocs,
  doc, updateDoc, deleteDoc, writeBatch, runTransaction, arrayRemove, arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase';
import { MessageSquare, Users, Plus, Send, ArrowLeft, X, Search, Trash2, Edit2 } from 'lucide-react';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

// Resolved live from the directory rather than stored as membership, so a new staff member belongs
// to "All Staff" the moment their profile exists -- nobody has to remember to update a list.
const PREDEFINED_GROUPS = [
  { id: '__all_staff', name: 'All Staff', match: (p) => p.kind === 'staff' },
  { id: '__all_teachers', name: 'All Teachers', match: (p) => p.kind === 'staff' && ['teacher', 'tch'].includes(p.systemRole) },
  { id: '__all_students', name: 'All Students', match: (p) => p.kind === 'student' },
];

const newThreadId = () =>
  `MSG-${Date.now().toString().slice(-6)}-${Math.floor(Math.random() * 100000)}`;

const messageDocId = (threadId, subId) => `${threadId}__${String(subId).padStart(4, '0')}`;

// Sender name colours in group chats, picked deterministically from the address so a person keeps
// the same colour across every conversation.
// Literal on purpose: these only have to be tellable apart from each other and legible on the
// message background. Pulling any of them from the brand tokens would make two senders share a
// colour the day the palette changes.
const SENDER_COLOURS = ['#1B4578', '#7A4E86', '#0F766E', '#B45309', '#9D174D', '#3F6212', '#5B21B6'];
const colourFor = (email) => {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) % 100000;
  return SENDER_COLOURS[h % SENDER_COLOURS.length];
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-CA') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const MyMessage = () => {
  const { activeSchoolId, isMobile, userProfile, user, closeSidebar } = useOutletContext();
  const { t } = useT();

  const ownEmail = (user?.email || '').toLowerCase();
  const ownRole = String(userProfile?.role || '').toLowerCase();
  const isStudent = ownRole === 'student';
  // userProfile is null on the first render after a reload, so isStudent is false for everyone at
  // that moment and "if (isStudent) return" reads as "carry on". Without this the staff-only
  // loaders below fire once for a student before their role is known.
  const roleKnown = !!userProfile && !!ownRole;
  // Staff are identified by initial, students by SID -- both display-only; `participants` always
  // holds emails, the one identifier Firestore rules can verify against request.auth.
  const ownId = (isStudent ? userProfile?.studentId : userProfile?.initial) || ownEmail;
  const ownName = userProfile?.name || ownId;

  const [activeTab, setActiveTab] = useState('messages');

  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [showParticipants, setShowParticipants] = useState(false);

  const [people, setPeople] = useState([]);
  const [customGroups, setCustomGroups] = useState([]);

  const [showCompose, setShowCompose] = useState(false);
  const [composeSearch, setComposeSearch] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);

  // --- Directory ------------------------------------------------------------
  // Students may only ever reply, so they never need a recipient list.
  useEffect(() => {
    if (!activeSchoolId || !roleKnown || isStudent) return;

    const load = async () => {
      const list = [];
      try {
        const sSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'));
        sSnap.forEach(d => {
          const data = d.data();
          const email = (data.email || d.id || '').toLowerCase();
          if (!email.includes('@') || email === ownEmail) return;
          const name = `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial || email;
          list.push({
            email, id: data.initial || email, name,
            kind: 'staff',
            systemRole: String(data.role || '').toLowerCase(),
            currentClass: '', classNumber: '',
            label: `${data.initial || email} — ${name}`,
            suffix: 'Staff',
            chip: data.initial || email,
          });
        });
      } catch (e) { console.error('Error loading staff directory', e); }

      try {
        const stSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'student_profiles'));
        stSnap.forEach(d => {
          const data = d.data();
          const email = (data.email || d.id || '').toLowerCase();
          if (!email.includes('@') || email === ownEmail) return;
          const name = `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.sid || email;
          list.push({
            email, id: data.sid || email, name,
            kind: 'student',
            systemRole: 'student',
            currentClass: data.currentClass || '', classNumber: data.classNumber || '',
            label: `${data.currentClass || '—'} (${data.classNumber || '—'}) - ${name} (${data.sid || ''})`,
            suffix: '',
            chip: data.currentClass ? `${data.currentClass}(${data.classNumber || '?'})` : (data.sid || email),
          });
        });
      } catch (e) { console.error('Error loading student directory', e); }

      list.sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === 'staff' ? -1 : 1;
        if (a.kind === 'student') {
          const cls = String(a.currentClass).localeCompare(String(b.currentClass), undefined, { numeric: true });
          if (cls !== 0) return cls;
          return (Number(a.classNumber) || 0) - (Number(b.classNumber) || 0);
        }
        return a.name.localeCompare(b.name);
      });
      setPeople(list);
    };

    load();
  }, [activeSchoolId, roleKnown, isStudent, ownEmail]);

  const peopleByEmail = useMemo(() => Object.fromEntries(people.map(p => [p.email, p])), [people]);

  const classOptions = useMemo(
    () => [...new Set(people.filter(p => p.kind === 'student' && p.currentClass).map(p => p.currentClass))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [people]
  );

  // --- Custom groups --------------------------------------------------------

  useEffect(() => {
    if (!activeSchoolId || !roleKnown || isStudent) return;
    const q = query(
      collection(db, 'schools', activeSchoolId, 'message_group'),
      where('owner', '==', ownEmail)
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCustomGroups(list);
    }, (e) => console.error('Error loading message groups', e));
    return () => unsub();
  }, [activeSchoolId, roleKnown, isStudent, ownEmail]);

  const resolveGroup = useCallback((groupId) => {
    const predefined = PREDEFINED_GROUPS.find(g => g.id === groupId);
    if (predefined) return people.filter(predefined.match).map(p => p.email);
    const custom = customGroups.find(g => g.uid === groupId);
    // Members who have since left the school are dropped rather than added to a chat as a dead address.
    return (custom?.memberEmails || []).filter(e => peopleByEmail[e]);
  }, [people, customGroups, peopleByEmail]);

  // --- Threads --------------------------------------------------------------

  useEffect(() => {
    if (!activeSchoolId || !ownEmail) return;
    setLoadingThreads(true);
    const q = query(
      collection(db, 'schools', activeSchoolId, 'message_thread'),
      where('participants', 'array-contains', ownEmail)
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => String(b.lastAt || '').localeCompare(String(a.lastAt || '')));
      setThreads(list);
      setLoadingThreads(false);
    }, (e) => { console.error('Error loading threads', e); setLoadingThreads(false); });
    return () => unsub();
  }, [activeSchoolId, ownEmail]);

  useEffect(() => {
    if (!activeSchoolId || !selectedThreadId) { setMessages([]); return; }
    // No orderBy: paired with the message_id equality filter it would need a composite index
    // deployed before the module worked at all. Threads are short, so sort client-side.
    const q = query(
      collection(db, 'schools', activeSchoolId, 'message_record'),
      where('message_id', '==', selectedThreadId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => (a.sub_id || 0) - (b.sub_id || 0));
      setMessages(list);
    }, (e) => console.error('Error loading messages', e));
    return () => unsub();
  }, [activeSchoolId, selectedThreadId]);

  const selectedThread = useMemo(
    () => threads.find(item => item.uid === selectedThreadId) || null,
    [threads, selectedThreadId]
  );

  const markThreadRead = useCallback(async (thread) => {
    if (!thread || !(thread.unreadFor || []).includes(ownEmail)) return;
    try {
      await updateDoc(doc(db, 'schools', activeSchoolId, 'message_thread', thread.uid), {
        unreadFor: arrayRemove(ownEmail),
      });
      // readBy is an array rather than a single timestamp: in a group chat a message has many
      // readers, and "who has seen this" is the question worth answering.
      const unseen = messages.filter(m => m.senderEmail !== ownEmail && !(m.readBy || []).includes(ownEmail));
      if (unseen.length) {
        const batch = writeBatch(db);
        unseen.slice(0, 400).forEach(m =>
          batch.update(doc(db, 'schools', activeSchoolId, 'message_record', m.uid), { readBy: arrayUnion(ownEmail) })
        );
        await batch.commit();
      }
    } catch (e) { console.error('Error marking thread read', e); }
  }, [activeSchoolId, ownEmail, messages]);

  useEffect(() => { if (selectedThread) markThreadRead(selectedThread); }, [selectedThread, markThreadRead]);

  // --- Sending --------------------------------------------------------------

  const handleCompose = async (e) => {
    e.preventDefault();
    if (!recipients.length || !composeBody.trim()) return;

    setSending(true);
    try {
      const now = new Date().toISOString();
      const body = composeBody.trim();
      const isGroup = recipients.length > 1;
      const participants = [ownEmail, ...recipients];
      const threadId = newThreadId();

      const defaultName = isGroup
        ? `Group (${participants.length})`
        : (peopleByEmail[recipients[0]]?.name || 'Conversation');
      const subject = composeSubject.trim() || defaultName;

      // One thread for everyone -- a group conversation, not a copy per recipient. Cost is two
      // writes no matter how many people are in it.
      const batch = writeBatch(db);
      batch.set(doc(db, 'schools', activeSchoolId, 'message_thread', threadId), {
        message_id: threadId,
        subject,
        isGroup,
        participants,
        createdBy: ownEmail,
        lastSubId: 0,
        lastAt: now,
        lastSnippet: body.slice(0, 120),
        lastSenderEmail: ownEmail,
        lastSenderId: ownId,
        unreadFor: recipients,
        createdAt: now,
      });
      batch.set(doc(db, 'schools', activeSchoolId, 'message_record', messageDocId(threadId, 0)), {
        message_id: threadId,
        sub_id: 0,
        subject,
        participants,
        senderEmail: ownEmail, senderId: ownId, senderName: ownName,
        // Kept for 1:1 so the original record shape still reads naturally; in a group the audience
        // is `participants`, so these stay blank rather than naming an arbitrary member.
        receiverEmail: isGroup ? '' : recipients[0],
        receiverId: isGroup ? '' : (peopleByEmail[recipients[0]]?.id || ''),
        receiverName: isGroup ? '' : (peopleByEmail[recipients[0]]?.name || ''),
        content: body,
        datetime: now,
        readBy: [],
      });
      await batch.commit();

      setShowCompose(false);
      setRecipients([]); setComposeSubject(''); setComposeBody(''); setComposeSearch('');
      setSelectedThreadId(threadId);
    } catch (err) {
      console.error('Error sending message', err);
      alert(t('myMessage.sendFailed', { message: err.message }));
    }
    setSending(false);
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!selectedThread || !replyBody.trim()) return;

    setSending(true);
    try {
      const threadRef = doc(db, 'schools', activeSchoolId, 'message_thread', selectedThread.uid);
      const now = new Date().toISOString();
      const body = replyBody.trim();

      // sub_id is allocated inside a transaction: computing "highest + 1" outside one means two
      // simultaneous replies write the same document id and one message is silently lost. In a
      // group chat several people may well be typing at once, so this matters more, not less.
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(threadRef);
        if (!snap.exists()) throw new Error(t('myMessage.gone'));
        const data = snap.data();
        const nextSubId = (data.lastSubId || 0) + 1;
        const others = (data.participants || []).filter(p => p !== ownEmail);

        tx.set(doc(db, 'schools', activeSchoolId, 'message_record', messageDocId(selectedThread.uid, nextSubId)), {
          message_id: selectedThread.uid,
          sub_id: nextSubId,
          subject: data.subject || '',
          participants: data.participants,
          senderEmail: ownEmail, senderId: ownId, senderName: ownName,
          receiverEmail: data.isGroup ? '' : (others[0] || ''),
          receiverId: '', receiverName: '',
          content: body,
          datetime: now,
          readBy: [],
        });

        tx.update(threadRef, {
          lastSubId: nextSubId, lastAt: now, lastSnippet: body.slice(0, 120),
          lastSenderEmail: ownEmail, lastSenderId: ownId,
          unreadFor: others,
        });
      });

      setReplyBody('');
    } catch (err) {
      console.error('Error replying', err);
      alert(t('myMessage.sendFailed', { message: err.message }));
    }
    setSending(false);
  };

  // --- Recipient selection --------------------------------------------------

  const toggleRecipient = (email) =>
    setRecipients(prev => (prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]));

  const addMany = (emails) => setRecipients(prev => [...new Set([...prev, ...emails])]);
  const addClass = (cls) => { if (cls) addMany(people.filter(p => p.kind === 'student' && p.currentClass === cls).map(p => p.email)); };
  const addGroup = (groupId) => { if (groupId) addMany(resolveGroup(groupId)); };

  // --- Message groups -------------------------------------------------------

  const openGroupModal = (group = null) => {
    setEditingGroupId(group?.uid || null);
    setGroupName(group?.name || '');
    setGroupMembers(group?.memberEmails || []);
    setGroupSearch('');
    setShowGroupModal(true);
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim() || groupMembers.length === 0) {
      alert(t('myMessage.groupNeedsMembers'));
      return;
    }
    setSavingGroup(true);
    try {
      const ref = editingGroupId
        ? doc(db, 'schools', activeSchoolId, 'message_group', editingGroupId)
        : doc(collection(db, 'schools', activeSchoolId, 'message_group'));
      const batch = writeBatch(db);
      batch.set(ref, {
        name: groupName.trim(),
        memberEmails: groupMembers,
        owner: ownEmail,
        updatedAt: new Date().toISOString(),
        updatedBy: ownEmail,
      }, { merge: true });
      await batch.commit();
      setShowGroupModal(false);
    } catch (err) {
      console.error('Error saving group', err);
      alert(t('myMessage.saveGroupFailed', { message: err.message }));
    }
    setSavingGroup(false);
  };

  const handleDeleteGroup = async (group) => {
    if (!group) return;
    if (!window.confirm(t('myMessage.deleteGroupConfirm', { name: group.name }))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'message_group', group.uid));
    } catch (err) {
      alert(t('myMessage.deleteFailed', { message: err.message }));
    }
  };

  // --- Dialog behaviour -----------------------------------------------------

  const anyDialogOpen = showCompose || showGroupModal;
  useEffect(() => {
    if (!anyDialogOpen) return;
    if (isMobile) closeSidebar?.();
    const onKey = (e) => {
      if (e.key !== 'Escape' || sending || savingGroup) return;
      setShowCompose(false);
      setShowGroupModal(false);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [anyDialogOpen, isMobile, closeSidebar, sending, savingGroup]);

  // --- Derived --------------------------------------------------------------

  const nameOf = (email) => {
    if (email === ownEmail) return t('myMessage.you');
    const p = peopleByEmail[email];
    return p ? p.name : email;
  };

  const threadTitle = (thread) => {
    if (thread.isGroup) return thread.subject || `Group (${(thread.participants || []).length})`;
    const other = (thread.participants || []).find(p => p !== ownEmail) || '';
    const person = peopleByEmail[other];
    return person ? person.label : other;
  };

  const matches = (p, term) => `${p.name} ${p.id} ${p.email} ${p.currentClass} ${p.classNumber}`.toLowerCase().includes(term);
  const filteredPeople = useMemo(() => {
    const q = composeSearch.trim().toLowerCase();
    return q ? people.filter(p => matches(p, q)) : people;
  }, [people, composeSearch]);
  const filteredGroupPeople = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    return q ? people.filter(p => matches(p, q)) : people;
  }, [people, groupSearch]);

  const unreadCount = threads.filter(item => (item.unreadFor || []).includes(ownEmail)).length;

  const showList = !isMobile || !selectedThreadId;
  const showConversation = !isMobile || !!selectedThreadId;

  const cardStyle = {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: isMobile ? 0 : 'var(--radius-lg)',
    boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
  };

  const allGroupOptions = [
    ...PREDEFINED_GROUPS.map(g => ({ id: g.id, name: t(`myMessage.predefined.${g.id}`, {}, g.name), count: people.filter(g.match).length, predefined: true })),
    ...customGroups.map(g => ({ id: g.uid, name: g.name, count: (g.memberEmails || []).length, predefined: false })),
  ];

  const peoplePicker = (list, selected, onToggle) => (
    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
      {list.map(p => {
        const checked = selected.includes(p.email);
        return (
          <label key={p.email} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.6rem', cursor: 'pointer', backgroundColor: checked ? 'var(--color-primary-light)' : 'transparent' }}>
            <input type="checkbox" checked={checked} onChange={() => onToggle(p.email)} style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: 'var(--color-primary)' }} />
            <span style={{ fontSize: '0.83rem' }}>
              {p.label}
              {p.suffix && <span style={{ color: 'var(--color-text-muted)' }}> · {p.suffix}</span>}
            </span>
          </label>
        );
      })}
      {list.length === 0 && <div style={{ padding: '0.75rem', color: 'var(--color-text-muted)', fontSize: '0.83rem' }}>{t('myMessage.noMatches')}</div>}
    </div>
  );

  const tabs = [
    { id: 'messages', label: unreadCount > 0 ? t('myMessage.tabs.messagesWithCount', { count: unreadCount }) : t('myMessage.tabs.messages'), icon: MessageSquare },
    ...(isStudent ? [] : [{ id: 'groups', label: t('myMessage.tabs.groups'), icon: Users }]),
  ];

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`theme-tab ${activeTab === tab.id ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* Students reply within chats staff opened; they never start one. */}
            {!isStudent && activeTab === 'messages' && (
              <button className="btn-primary" onClick={() => setShowCompose(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                <Plus size={15} /> {t('myMessage.newMessage')}
              </button>
            )}
            {!isStudent && activeTab === 'groups' && (
              <button className="btn-primary" onClick={() => openGroupModal()} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                <Plus size={15} /> {t('myMessage.newGroup')}
              </button>
            )}
          </div>
        </HeaderActionsPortal>
      </div>

      {/* ---------------- MESSAGES ---------------- */}
      {activeTab === 'messages' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', gap: isMobile ? 0 : '1rem', padding: isMobile ? 0 : '0 1.5rem 1.5rem' }}>
          {showList && (
            <div style={{ ...cardStyle, width: isMobile ? '100%' : '340px', flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto' }}>
              {loadingThreads ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
              ) : threads.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                  {isStudent ? t('myMessage.noThreadsStudent') : t('myMessage.noThreadsStaff')}
                </div>
              ) : threads.map(thread => {
                const unread = (thread.unreadFor || []).includes(ownEmail);
                const active = thread.uid === selectedThreadId;
                return (
                  <button
                    key={thread.uid}
                    onClick={() => { setSelectedThreadId(thread.uid); setShowParticipants(false); }}
                    style={{
                      textAlign: 'left', border: 'none', cursor: 'pointer', width: '100%',
                      padding: '0.85rem 1rem', borderBottom: '1px solid var(--color-border)',
                      backgroundColor: active ? 'var(--color-primary-light)' : 'transparent',
                      display: 'flex', flexDirection: 'column', gap: '0.2rem',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'baseline' }}>
                      <span style={{ fontWeight: unread ? '700' : '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.35rem', minWidth: 0 }}>
                        {thread.isGroup && <Users size={13} style={{ flexShrink: 0 }} />}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{threadTitle(thread)}</span>
                      </span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{formatWhen(thread.lastAt)}</span>
                    </div>
                    {thread.isGroup && (
                      <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{t('myMessage.participants', { count: (thread.participants || []).length })}</span>
                    )}
                    <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {thread.lastSenderEmail === ownEmail
                        ? t('myMessage.fromYou', { snippet: thread.lastSnippet })
                        : (thread.isGroup
                          ? t('myMessage.fromSomeone', { name: thread.lastSenderId || nameOf(thread.lastSenderEmail), snippet: thread.lastSnippet })
                          : thread.lastSnippet)}
                    </span>
                    {unread && <span style={{ alignSelf: 'flex-start', fontSize: '0.65rem', fontWeight: '700', color: 'var(--color-primary)' }}>● {t('myMessage.unread')}</span>}
                  </button>
                );
              })}
            </div>
          )}

          {showConversation && (
            <div style={{ ...cardStyle, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {!selectedThread ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}>{t('myMessage.selectOne')}</div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.85rem 1rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
                    {isMobile && (
                      <button onClick={() => setSelectedThreadId(null)} aria-label={t('myMessage.back')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: '0.25rem', color: 'var(--color-text-main)' }}>
                        <ArrowLeft size={20} />
                      </button>
                    )}
                    <button
                      onClick={() => selectedThread.isGroup && setShowParticipants(v => !v)}
                      style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', minWidth: 0, flex: 1, cursor: selectedThread.isGroup ? 'pointer' : 'default' }}
                    >
                      <div style={{ fontWeight: '700', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{threadTitle(selectedThread)}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedThread.isGroup
                          ? (selectedThread.participants || []).map(nameOf).join(', ')
                          : selectedThread.subject}
                      </div>
                    </button>
                  </div>

                  {showParticipants && selectedThread.isGroup && (
                    <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)', maxHeight: '160px', overflowY: 'auto', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: '700', color: 'var(--color-text-muted)', marginBottom: '0.35rem' }}>
                        {t('myMessage.participantsHeading', { count: (selectedThread.participants || []).length })}
                      </div>
                      {(selectedThread.participants || []).map(email => (
                        <div key={email} style={{ fontSize: '0.8rem', padding: '0.15rem 0' }}>
                          {peopleByEmail[email]?.label || (email === ownEmail ? `${ownId} — ${ownName}` : email)}
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {messages.map((m, idx) => {
                      const mine = m.senderEmail === ownEmail;
                      // WhatsApp-style: in a group, name the sender above their bubble, but only when
                      // the speaker changes -- repeating it on every consecutive message is noise.
                      const showSender = selectedThread.isGroup && !mine
                        && (idx === 0 || messages[idx - 1].senderEmail !== m.senderEmail);
                      const readers = (m.readBy || []).length;
                      return (
                        <div key={m.uid} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
                          {showSender && (
                            <div style={{ fontSize: '0.72rem', fontWeight: '700', color: colourFor(m.senderEmail), marginBottom: '0.15rem' }}>
                              {m.senderName || m.senderId}
                            </div>
                          )}
                          <div style={{
                            backgroundColor: mine ? 'var(--color-primary)' : 'var(--color-bg-app)',
                            color: mine ? '#fff' : 'var(--color-text-main)',
                            padding: '0.6rem 0.85rem', borderRadius: 'var(--radius-md)',
                            fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          }}>
                            {m.content}
                          </div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', marginTop: '0.2rem', textAlign: mine ? 'right' : 'left' }}>
                            #{m.sub_id} · {mine ? t('myMessage.you') : m.senderId} · {formatWhen(m.datetime)}
                            {mine && readers > 0 ? ` · ${t('myMessage.readBy', { count: readers })}` : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <form onSubmit={handleReply} style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1rem', borderTop: '1px solid var(--color-border)', flexShrink: 0 }}>
                    <input className="input-field" value={replyBody} onChange={e => setReplyBody(e.target.value)} placeholder={t('myMessage.write')} style={{ margin: 0, flex: 1 }} />
                    <button type="submit" className="btn-primary" disabled={sending || !replyBody.trim()} style={{ width: 'auto', padding: '0.55rem 1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Send size={15} /> {t('myMessage.send')}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---------------- MESSAGE GROUPS ---------------- */}
      {activeTab === 'groups' && !isStudent && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '0.75rem' : '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: 0 }}>
            {t('myMessage.groupsIntro')}
          </p>

          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            {allGroupOptions.map(g => {
              const custom = customGroups.find(c => c.uid === g.id);
              return (
                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1rem', borderBottom: '1px solid var(--color-border)' }}>
                  <Users size={16} color="var(--color-primary)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                      {g.name}
                      {g.predefined && <span style={{ marginLeft: '0.5rem', fontSize: '0.68rem', fontWeight: '700', color: 'var(--color-text-muted)' }}>{t('myMessage.builtIn')}</span>}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{t('myMessage.memberCount', { count: g.count })}</div>
                  </div>
                  {!g.predefined && (
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      <button onClick={() => openGroupModal(custom)} title={t('common.edit')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', padding: '0.25rem' }}><Edit2 size={16} /></button>
                      <button onClick={() => handleDeleteGroup(custom)} title={t('common.delete')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0.25rem' }}><Trash2 size={16} /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------------- COMPOSE DIALOG ---------------- */}
      {showCompose && (
        <div onClick={() => { if (!sending) setShowCompose(false); }} style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '640px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>
                {recipients.length > 1 ? t('myMessage.newGroupChat') : t('myMessage.newMessage')}
              </h3>
              <button type="button" onClick={() => setShowCompose(false)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}><X size={20} /></button>
            </div>

            <form onSubmit={handleCompose} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('myMessage.participantsLabel', { count: recipients.length })}</label>

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  <select className="input-field" value="" onChange={e => { addGroup(e.target.value); e.target.value = ''; }} style={{ margin: 0, flex: '1 1 160px' }}>
                    <option value="">{t('myMessage.addGroup')}</option>
                    {allGroupOptions.map(g => <option key={g.id} value={g.id}>{g.name} ({g.count})</option>)}
                  </select>
                  <select className="input-field" value="" onChange={e => { addClass(e.target.value); e.target.value = ''; }} style={{ margin: 0, flex: '1 1 140px' }}>
                    <option value="">{t('myMessage.addClass')}</option>
                    {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {recipients.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem', maxHeight: '110px', overflowY: 'auto' }}>
                    {recipients.map(email => {
                      const p = peopleByEmail[email];
                      return (
                        <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)', borderRadius: '999px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: '600' }}>
                          {p ? p.chip : email}
                          <button type="button" onClick={() => toggleRecipient(email)} aria-label={`Remove ${p ? p.chip : email}`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex', padding: 0 }}>
                            <X size={12} />
                          </button>
                        </span>
                      );
                    })}
                    <button type="button" onClick={() => setRecipients([])} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', fontSize: '0.72rem', fontWeight: '600' }}>{t('myMessage.clearAll')}</button>
                  </div>
                )}

                <div style={{ position: 'relative', marginBottom: '0.4rem' }}>
                  <Search size={15} style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
                  <input className="input-field" value={composeSearch} onChange={e => setComposeSearch(e.target.value)} placeholder={t('myMessage.searchPeople')} style={{ margin: 0, paddingLeft: '2rem' }} />
                </div>

                {peoplePicker(filteredPeople, recipients, toggleRecipient)}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>
                  {recipients.length > 1 ? t('myMessage.groupName') : t('myMessage.subject')}
                </label>
                <input className="input-field" value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder={t('myMessage.optional')} style={{ margin: 0 }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('myMessage.message')}</label>
                <textarea className="input-field" value={composeBody} onChange={e => setComposeBody(e.target.value)} rows={5} required style={{ margin: 0, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              {recipients.length > 1 && (
                <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                  {t('myMessage.groupChatWarning')}
                </p>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={() => setShowCompose(false)} disabled={sending} style={{ width: 'auto', padding: '0.55rem 1rem' }}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={sending || recipients.length === 0 || !composeBody.trim()} style={{ width: 'auto', padding: '0.55rem 1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Send size={15} /> {sending ? t('myMessage.sending') : t('myMessage.send')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- GROUP DIALOG ---------------- */}
      {showGroupModal && (
        <div onClick={() => { if (!savingGroup) setShowGroupModal(false); }} style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '560px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{editingGroupId ? t('myMessage.editGroup') : t('myMessage.newGroup')}</h3>
              <button type="button" onClick={() => setShowGroupModal(false)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}><X size={20} /></button>
            </div>

            <form onSubmit={handleSaveGroup} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('myMessage.groupName')}</label>
                <input className="input-field" value={groupName} onChange={e => setGroupName(e.target.value)} placeholder={t('myMessage.groupNamePlaceholder')} required style={{ margin: 0 }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('myMessage.members', { count: groupMembers.length })}</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <select
                    className="input-field"
                    value=""
                    onChange={e => {
                      const cls = e.target.value;
                      if (cls) setGroupMembers(prev => [...new Set([...prev, ...people.filter(p => p.kind === 'student' && p.currentClass === cls).map(p => p.email)])]);
                      e.target.value = '';
                    }}
                    style={{ margin: 0, flex: 1 }}
                  >
                    <option value="">{t('myMessage.addClass')}</option>
                    {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {groupMembers.length > 0 && (
                    <button type="button" className="btn-outline" onClick={() => setGroupMembers([])} style={{ width: 'auto', padding: '0.55rem 0.9rem' }}>{t('myMessage.clear')}</button>
                  )}
                </div>
                <input className="input-field" value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder={t('myMessage.searchStaffStudents')} style={{ margin: 0, marginBottom: '0.4rem' }} />
                {peoplePicker(filteredGroupPeople, groupMembers, (email) =>
                  setGroupMembers(prev => prev.includes(email) ? prev.filter(x => x !== email) : [...prev, email])
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={() => setShowGroupModal(false)} disabled={savingGroup} style={{ width: 'auto', padding: '0.55rem 1rem' }}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={savingGroup} style={{ width: 'auto', padding: '0.55rem 1rem' }}>{savingGroup ? t('myMessage.saving') : t('myMessage.saveGroup')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyMessage;
